import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { formatFileSize } from "../../features/materials/utils/formatFileSize";
import { MaterialAudience } from "../../features/portal/enums/MaterialAudience";
import { selectPortalMaterials } from "../../features/portal/portal-slice";
import {
  deletePortalMaterial,
  fetchPortalMaterials,
  uploadPortalMaterial,
} from "../../features/portal/slice-extensions/portal-slice-extension";
import { PortalMaterialGroup, groupPortalMaterials } from "../../features/portal/utils/portalMaterialGroups";
import PortalMaterialRow from "./PortalMaterialRow";

interface PortalMaterialsCardProps {
  memberId: string;
  canUpload: boolean;
  /** The ward's first name when a guardian looks at a ward's library; null for the member's own. */
  wardName: string | null;
}

/**
 * The files shared with the member, grouped by who they were shared with (them, each of their
 * groups, everyone). Viewable files open in place; every file downloads; own uploads can be removed.
 */
const PortalMaterialsCard = ({ memberId, canUpload, wardName }: PortalMaterialsCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const materials = useAppSelector(selectPortalMaterials(memberId));
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchPortalMaterials({ memberId }));
  }, [dispatch, memberId]);

  const groups = useMemo(() => groupPortalMaterials(materials ?? []), [materials]);
  const categories = useMemo(
    () => Array.from(new Set((materials ?? []).map((material) => material.category))).sort(),
    [materials],
  );

  const acceptFile = (file: File) => {
    setPendingFile(file);
    setCategory("");
    setDescription("");
    setError(null);
  };

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      acceptFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    if (!canUpload || busy) return;
    const file = event.dataTransfer.files?.[0];
    if (file) {
      acceptFile(file);
    }
  };

  const handleUpload = async () => {
    if (!pendingFile) return;
    setBusy(true);
    setError(null);
    const result = await dispatch(
      uploadPortalMaterial({
        memberId,
        file: pendingFile,
        category: category || undefined,
        description: description || undefined,
      }),
    );
    setBusy(false);
    if (uploadPortalMaterial.fulfilled.match(result)) {
      setPendingFile(null);
    } else {
      setError(result.payload?.message ?? t("portal.materials.uploadFailed", "Could not upload the file."));
    }
  };

  const closeViewer = useCallback(() => setOpenId(null), []);

  const handleDelete = async (materialId: string) => {
    setError(null);
    const result = await dispatch(deletePortalMaterial({ memberId, materialId }));
    if (!deletePortalMaterial.fulfilled.match(result)) {
      setError(result.payload?.message ?? t("portal.materials.deleteFailed", "Could not remove the file."));
    }
  };

  const groupHeading = (group: PortalMaterialGroup): string => {
    if (group.audience === MaterialAudience.Member) {
      return wardName ? t("portal.library.sharedWithMember", { name: wardName }) : t("portal.library.sharedWithYou");
    }
    if (group.audience === MaterialAudience.Group) {
      return t("portal.library.sharedWithGroup", { group: group.groupName ?? "" });
    }
    return t("portal.library.sharedWithEveryone");
  };

  return (
    // The whole card doubles as a drop target when uploads are allowed.
    <div
      className={`with-border rounded-2xl bg-white p-4 dark:bg-primary-10 ${
        dragOver ? "border-primary-40 bg-primary-95 dark:border-primary-90 dark:bg-primary-20" : ""
      }`}
      onDragOver={(event) => {
        if (!canUpload) return;
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {canUpload && !pendingFile && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-grey-40 dark:text-grey-80">
            {t("portal.materials.dropHint", "Tip: you can also drop a file anywhere on this card.")}
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <Upload size={14} />
            {t("portal.materials.uploadAction", "Upload")}
          </button>
          <input type="file" className="hidden" ref={fileInputRef} onChange={handleFilePicked} />
        </div>
      )}

      {pendingFile && (
        <div className="mt-3 rounded-lg bg-grey-95 p-3 dark:bg-primary-20">
          <p className="truncate text-sm font-medium text-grey-20 dark:text-white">
            {pendingFile.name} · {formatFileSize(pendingFile.size)}
          </p>
          <input
            type="text"
            className="input mt-2"
            value={category}
            maxLength={100}
            list="portal-material-category-options"
            placeholder={t("portal.materials.categoryPlaceholder", "Category (optional)")}
            onChange={(e) => setCategory(e.target.value)}
            disabled={busy}
          />
          <datalist id="portal-material-category-options">
            {categories.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          <input
            type="text"
            className="input mt-2"
            value={description}
            maxLength={500}
            placeholder={t("portal.materials.descriptionPlaceholder", "Description (optional)")}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setPendingFile(null)} disabled={busy}>
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={busy}>
              {busy
                ? t("portal.materials.uploading", "Uploading…")
                : t("portal.materials.uploadAction", "Upload")}
            </button>
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <p className={`${canUpload ? "mt-3" : ""} text-sm text-grey-40 dark:text-grey-80`}>{t("portal.library.empty")}</p>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="mt-4 first:mt-0" data-testid="portal-material-group">
            <h2 className="text-sm font-semibold text-grey-20 dark:text-white">{groupHeading(group)}</h2>
            <ul className="mt-2 divide-y divide-grey-90 border-t border-grey-90 dark:divide-primary-20 dark:border-primary-20">
              {group.materials.map((material) => (
                <PortalMaterialRow
                  key={material.id}
                  memberId={memberId}
                  material={material}
                  canDelete={canUpload && material.isOwnUpload}
                  isOpen={openId === material.id}
                  onOpen={() => setOpenId(material.id)}
                  onClose={closeViewer}
                  onDelete={() => handleDelete(material.id)}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      {error && <p className="field-error mt-2">{error}</p>}
    </div>
  );
};

export default PortalMaterialsCard;
